import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for Google OAuth callback (query params)
      const queryParams = new URLSearchParams(window.location.search);
      const googleToken = queryParams.get("token");
      const provider = queryParams.get("provider");

      if (googleToken && provider === "google") {
        try {
          localStorage.setItem("session_token", googleToken);
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${googleToken}` }
          });
          login(response.data);
          toast.success("Signed in with Google!");
          window.history.replaceState(null, "", "/me");
          const storedRedirect = localStorage.getItem('auth_redirect');
          localStorage.removeItem('auth_redirect');
          navigate(storedRedirect || "/me", { replace: true });
        } catch (error) {
          console.error("Google auth error:", error);
          toast.error("Google sign-in failed");
          navigate("/login");
        }
        return;
      }

      // Original: Hash-based session_id auth
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        login(response.data);
        toast.success("Welcome! Let's customize your experience");
        
        window.history.replaceState(null, "", window.location.pathname);
        
        // Check if there's a stored redirect destination (e.g., from admin login)
        const storedRedirect = localStorage.getItem('auth_redirect');
        localStorage.removeItem('auth_redirect');
        
        const redirectTo = storedRedirect || "/me";
        navigate(redirectTo, { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
        <p className="text-gray-600 font-mono text-sm">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
